// Widget configuration
export interface WidgetConfig {
  id: string;
  name: string;
  icon: string;
  apiEndpoint: string;
  drillDownUrl: string;
  enabled: boolean;
}

// Widget data returned from API
export interface WidgetData {
  status: 'healthy' | 'warning' | 'critical' | 'loading' | 'error';
  lastUpdated: string;
  stats: WidgetStat[];
  alerts: WidgetAlert[];
}

export interface WidgetStat {
  label: string;
  value: string | number;
  type?: 'info' | 'success' | 'warning' | 'critical' | 'orange';
}

export interface WidgetAlert {
  severity: 'info' | 'warning' | 'critical';
  message: string;
  timestamp: string;
}

// Combined alert with source info
export interface CombinedAlert extends WidgetAlert {
  source: string;
  sourceId: string;
}

// Settings
export interface HudSettings {
  refreshInterval: number;
  openInNewWindow: boolean;
  widgetUrls: Record<string, { api: string; drillDown: string }>;
  widgetApiKeys: Record<string, string>;  // API keys for widgets requiring authentication
}

// Default widget configurations
// Order: Row 1: TAM, 365mgmt, serverhealth, autotask | Row 2: itglue, screenconnect, zoneshift
export const defaultWidgets: WidgetConfig[] = [
  {
    id: 'tam',
    name: 'Client Operations',
    icon: '📊',
    apiEndpoint: '/api/proxy/tam',  // Secure server-side proxy (API key stored in Azure env vars)
    drillDownUrl: 'https://nice-moss-0d608e510.2.azurestaticapps.net?embed=true',
    enabled: true,
  },
  {
    id: '365mgmt',
    name: '365 Management',
    icon: '☁️',
    apiEndpoint: '/api/proxy/365mgmt',  // Secure server-side proxy (API key stored in Azure env vars)
    drillDownUrl: 'https://365mgmt.umetech.net',  // Full app with authentication
    enabled: true,
  },
  {
    id: 'serverhealth',
    name: 'Server Health',
    icon: '🖥️',
    apiEndpoint: '/api/proxy/serverhealth',  // Secure server-side proxy (API key stored in Azure env vars)
    drillDownUrl: 'https://agreeable-coast-0b06cc41e.6.azurestaticapps.net',
    enabled: true,
  },
  {
    id: 'autotask',
    name: 'AutoTask Tickets',
    icon: '🎫',
    apiEndpoint: '/api/proxy/autotask',  // Secure server-side proxy (API key stored in Azure env vars)
    drillDownUrl: 'https://lively-dune-036b62e0f.4.azurestaticapps.net',  // Full app
    enabled: true,
  },
  {
    id: 'itglue',
    name: 'ITGlue Activity',
    icon: '📋',
    apiEndpoint: 'https://white-moss-0f9274e1e.3.azurestaticapps.net/api/widget-summary',
    drillDownUrl: 'https://white-moss-0f9274e1e.3.azurestaticapps.net',
    enabled: true,
  },
  {
    id: 'screenconnect',
    name: 'ScreenConnect Security',
    icon: '🛡️',
    apiEndpoint: '/api/proxy/screenconnect',
    drillDownUrl: 'https://victorious-pond-0e0b08e1e.1.azurestaticapps.net',
    enabled: true,
  },
  {
    id: 'zoneshift',
    name: 'ZoneShift',
    icon: '🌐',
    apiEndpoint: '',  // Phase 5: /api/proxy/zoneshift when widget API is ready
    drillDownUrl: 'https://zoneshift.umetech.net',
    enabled: true,
  },
];

// Placeholder widgets for future tools
export const placeholderWidgets: WidgetConfig[] = [
  {
    id: 'azure',
    name: 'Azure Health',
    icon: '☁️',
    apiEndpoint: '',
    drillDownUrl: '',
    enabled: false,
  },
  {
    id: 'm365',
    name: 'M365 Monitor',
    icon: '📧',
    apiEndpoint: '',
    drillDownUrl: '',
    enabled: false,
  },
  {
    id: 'backup',
    name: 'Backup Status',
    icon: '💾',
    apiEndpoint: '',
    drillDownUrl: '',
    enabled: false,
  },
  // 'tickets' placeholder removed - replaced by real 'autotask' widget
];

// Default settings
export const defaultSettings: HudSettings = {
  refreshInterval: 60,
  openInNewWindow: true,
  widgetUrls: {
    tam: {
      api: '/api/proxy/tam',  // Secure server-side proxy
      drillDown: 'https://nice-moss-0d608e510.2.azurestaticapps.net?embed=true',
    },
    '365mgmt': {
      api: '/api/proxy/365mgmt',  // Secure server-side proxy
      drillDown: 'https://365mgmt.umetech.net',  // Full app with authentication
    },
    serverhealth: {
      api: '/api/proxy/serverhealth',  // Secure server-side proxy
      drillDown: 'https://agreeable-coast-0b06cc41e.6.azurestaticapps.net',
    },
    autotask: {
      api: '/api/proxy/autotask',  // Secure server-side proxy
      drillDown: 'https://lively-dune-036b62e0f.4.azurestaticapps.net',  // Full app
    },
    itglue: {
      api: 'https://white-moss-0f9274e1e.3.azurestaticapps.net/api/widget-summary',
      drillDown: 'https://white-moss-0f9274e1e.3.azurestaticapps.net',
    },
    screenconnect: {
      api: '/api/proxy/screenconnect',
      drillDown: 'https://victorious-pond-0e0b08e1e.1.azurestaticapps.net',
    },
    zoneshift: {
      api: '',  // Phase 5: /api/proxy/zoneshift when widget API is ready
      drillDown: 'https://zoneshift.umetech.net',
    },
  },
  widgetApiKeys: {
    // All widgets now use secure server-side proxy - no client-side API keys needed
  },
};
